import { __awaiter, __generator } from "tslib";
import { VoiceCommandResultV2, WordVoiceCommandIds } from '@augloop-types/voice';
import * as AugLoopUtils from './utils/AugLoopUtils';
import { sendSpeechToTextFinalResultToSpeechClientObserver } from './SpeechToTextClient';
import { getValidCommandSet, hasValidCommandValues, alterCommandValue, ExperimentalCommandSetNames, } from './utils/validCommandUtil';
import { ULS, TraceLevel, LogCategory } from '@ms/office-online-telemetry';
function resolveExperimentalCommandSetNames(enabledExperimentalCommandSetNames) {
    // HACK: If caller sent enabledExperimentalCommandSetNames as a boolean, convert it to a names array.
    // This will be removed when WAC is updated to send command set names instead of booleans.
    if (typeof enabledExperimentalCommandSetNames === 'boolean') {
        if (enabledExperimentalCommandSetNames) {
            return [ExperimentalCommandSetNames.Tables];
        }
        return [];
    }
    return enabledExperimentalCommandSetNames;
}
// Represents a will receive voice commands from AL.
var VoiceCommandingClient = /** @class */ (function () {
    function VoiceCommandingClient(alRuntimeManager, fdVoiceCommandClientObserver, fdSpeechClientObserver, enabledExperimentalCommandSetNames) {
        var _this = this;
        this.voiceCommandRequestToken = null;
        this.alRuntimeManager = alRuntimeManager;
        this.fdVoiceCommandClientObserver = fdVoiceCommandClientObserver;
        this.fdSpeechClientObserver = fdSpeechClientObserver;
        this.validVoiceCommands = getValidCommandSet(resolveExperimentalCommandSetNames(enabledExperimentalCommandSetNames));
        this.annotationObserver = {
            onAnnotationResult: function (op) {
                _this.onAnnotationResult(op);
            },
        };
    }
    // Initialize voice commanding client.
    VoiceCommandingClient.prototype.initializeAsync = function (sessionTelemetry) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var _b, err_1, msg;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (this.voiceCommandRequestToken !== null) {
                            throw new Error('VoiceCommandingClient was already initialized.');
                        }
                        if (sessionTelemetry)
                            this.sessionTelemetry = sessionTelemetry;
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        _b = this;
                        return [4 /*yield*/, AugLoopUtils.activateAnnotationAsync(this.alRuntimeManager, VoiceCommandResultV2.getTypeName())];
                    case 2:
                        _b.voiceCommandRequestToken = _c.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _c.sent();
                        msg = 'VoiceCommandingClient.initializeAsync - Activate Annotations - ' + err_1.message;
                        /**
                         * As long as AL Websocket timeout, AL will point client to a new Websocket. However, AL sub/pub keeps sending.
                         * the client "Timeout waiting for response" error message even client is dictation, which doesn't impact the dictation working.
                         * The issue needs to be fixed from infrastructural side,
                         * AL has an User Story to fix the error: https://office.visualstudio.com/DefaultCollection/GSX/_workitems/edit/4779001
                         */
                        if (msg.includes('Timeout waiting for response')) {
                            ULS.sendTraceTag(0x220a2259 /* tag_8c8jz */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, msg + ' - Error stack: ' + err_1.stack);
                        }
                        else {
                            (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.onAugloopError(msg);
                            this.fdSpeechClientObserver.onError(AugLoopUtils.AugloopError.ErrorAugloopActivateAnnotations);
                            ULS.sendTraceTag(0x223096d1 /* tag_8mj1r */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Error, msg + ' - Error stack: ' + err_1.stack);
                        }
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    // Uninitialize voice commanding client.
    VoiceCommandingClient.prototype.uninitializeAsync = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var err_2, msg;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.voiceCommandRequestToken !== null)) return [3 /*break*/, 5];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, AugLoopUtils.releaseAnnotations(this.alRuntimeManager, [this.voiceCommandRequestToken])];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        err_2 = _b.sent();
                        msg = 'VoiceCommandingClient.uninitializeAsync - Release Annotations - ' + err_2.message;
                        (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.onAugloopError(msg);
                        ULS.sendTraceTag(0x223096d2 /* tag_8mj1s */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Error, 'VoiceCommandingClient.uninitializeAsync - Release Annotations - Error message: ' +
                            msg +
                            ' - Error stack: ' +
                            err_2.stack);
                        return [3 /*break*/, 4];
                    case 4:
                        this.voiceCommandRequestToken = null;
                        _b.label = 5;
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    // Return an annotation observer implemented by us that app will use to forward annotations to us.
    VoiceCommandingClient.prototype.getAnnotationObserver = function () {
        return this.annotationObserver;
    };
    // Handle an annotation result.
    VoiceCommandingClient.prototype.onAnnotationResult = function (op) {
        var _this = this;
        var voiceCommandItems = AugLoopUtils.filterOperationByAnnotationTypeName(op, VoiceCommandResultV2.getTypeName());
        voiceCommandItems.forEach(function (item) {
            _this.handleCommandResult(item.body);
        });
    };
    /** Handler for voice command V2 format */
    VoiceCommandingClient.prototype.handleCommandResult = function (commandResult) {
        if (commandResult.error && this.sessionTelemetry) {
            this.sessionTelemetry.onCommandError("handleCommandResult:" + commandResult.error);
        }
        for (var _i = 0, _a = commandResult.commandList; _i < _a.length; _i++) {
            var command = _a[_i];
            var isCommandSupported = this.validVoiceCommands.has(command.commandId) && hasValidCommandValues(command);
            // If type is InsertText or if this command is not supported, show it as a text
            if (command.commandId === WordVoiceCommandIds.InsertText || !isCommandSupported) {
                sendSpeechToTextFinalResultToSpeechClientObserver(this.fdSpeechClientObserver, commandResult.textAnnotation, command.sourceText, // FinalText
                this.sessionTelemetry);
            }
            else {
                // alter command if necessary
                var alteredCommand = alterCommandValue(command);
                this.fdVoiceCommandClientObserver.onVoiceCommand(alteredCommand);
            }
        }
    };
    return VoiceCommandingClient;
}());
export { VoiceCommandingClient };
//# sourceMappingURL=VoiceCommandingClient.js.map